import { Star, BellSimple, ChartLine } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { ChangeIndicator } from "./ChangeIndicator";
import { Sparkline } from "./Sparkline";
import { fmtNum, fmtMoney } from "../lib/format";
import { useFavorites } from "../hooks/useFavorites";
import { useAuth } from "../context/AuthContext";

export function AssetCard({ asset, index = 0, reversed = false }) {
  const nav = useNavigate();
  const { isFav, toggle } = useFavorites();
  const { user } = useAuth();
  const base = user?.base_currency || "USD";
  const isCrypto = asset.type === "crypto";
  const fav = isFav(isCrypto ? "crypto" : "currency", asset.symbol, asset.coinId);

  const favPayload = isCrypto
    ? { asset_type: "crypto", symbol: asset.symbol, name: asset.name, coin_id: asset.coinId }
    : { asset_type: "currency", symbol: asset.symbol, name: asset.name };

  const detailPath = isCrypto ? `/crypto/${asset.coinId}` : `/currency/${asset.symbol}`;
  const testid = `asset-card-${asset.symbol.toLowerCase()}`;

  return (
    <div
      data-testid={testid}
      className="group relative border border-border bg-card p-5 hover-lift cursor-pointer fade-up"
      style={{ animationDelay: `${index * 40}ms` }}
      onClick={() => nav(detailPath)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-heading text-lg font-bold tracking-tight">{asset.symbol}</span>
            {isCrypto && asset.image && <img src={asset.image} alt="" className="h-5 w-5 rounded-full" />}
          </div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">{asset.name}</div>
        </div>
        <button
          data-testid={`fav-btn-${asset.symbol.toLowerCase()}`}
          onClick={(e) => { e.stopPropagation(); toggle(favPayload); }}
          className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
          aria-label="Toggle favorite"
        >
          <Star size={20} weight={fav ? "fill" : "regular"} className={fav ? "text-primary" : ""} />
        </button>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="min-w-0">
          {isCrypto ? (
            <div className="font-mono text-2xl font-bold tracking-tight" data-testid={`price-${asset.symbol.toLowerCase()}`}>
              {fmtMoney(asset.price, base)}
            </div>
          ) : (
            <div className="font-mono text-xl font-bold tracking-tight" data-testid={`rate-${asset.symbol.toLowerCase()}`}>
              {reversed ? (
                <>
                  <span className="text-xs text-muted-foreground font-sans mr-1">1 {asset.symbol} =</span>
                  {fmtNum(asset.price ? 1 / asset.price : null, { digits: 6 })} <span className="text-sm text-muted-foreground">{base}</span>
                </>
              ) : (
                <>
                  <span className="text-xs text-muted-foreground font-sans mr-1">1 {base} =</span>
                  {fmtNum(asset.price)} <span className="text-sm text-muted-foreground">{asset.symbol}</span>
                </>
              )}
            </div>
          )}
        </div>
        {asset.sparkline && asset.sparkline.length > 1 && (
          <Sparkline points={asset.sparkline} width={96} height={32} className="shrink-0 opacity-90" />
        )}
      </div>

      <div className="mt-2 flex items-center justify-between">
        <ChangeIndicator value={asset.change} />
        <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
          <ChartLine size={16} className="text-muted-foreground" />
          <BellSimple size={16} className="text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}
